import { useQuery } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { BigNumber } from 'ethers';
import { Address } from 'viem';

import { LaunchpadSalePoolType } from '@api';

import { wagmiConfig } from '@shared/common/providers/Web3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, LAUNCHPAD_PRO_RATA_CONTRACT } from '@shared/constants';
import { weiToNumber } from '@shared/helpers/web3/formatters/numberToWei';

import { isProRataType } from '@launchpad/utils/contract';

import useSelfDepositedAmountQuery from './useSelfDepositedAmountQuery';

const BOOST_COEFFICIENT = 5;

const getTotalBuyingPower = async ({
  type,
  depositContractAddress,
}: {
  type?: LaunchpadSalePoolType;
  depositContractAddress: string | null | undefined;
}) => {
  if (!isProRataType(type)) {
    return 0;
  }

  const nonBoostAmount = await readContract(wagmiConfig, {
    ...LAUNCHPAD_PRO_RATA_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    address: depositContractAddress as Address,
    functionName: 'totalBaseNonBoostDeposited',
  });

  const boostAmount = await readContract(wagmiConfig, {
    ...LAUNCHPAD_PRO_RATA_CONTRACT,
    chainId: DEFAULT_TARGET_CHAIN.id,
    address: depositContractAddress as Address,
    functionName: 'totalBaseBoostDeposited',
  });

  return BigNumber.from(boostAmount)
    .mul(BigNumber.from(BOOST_COEFFICIENT))
    .add(BigNumber.from(nonBoostAmount));
};

const getMyBuyingPower = (amount = 0, boosted = false) => {
  if (!boosted) {
    return amount;
  }

  return amount * BOOST_COEFFICIENT;
};

export const useTotalBuyingPower = ({
  depositContractAddress,
  type,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
}) => {
  const { data: selfDeposit } = useSelfDepositedAmountQuery({ depositContractAddress, type });

  return useQuery({
    queryFn: () => getTotalBuyingPower({ type, depositContractAddress }),
    select: (data) => weiToNumber(data || 0, selfDeposit?.baseDepositDecimals),
    queryKey: ['totalBuyingPower', depositContractAddress, type, selfDeposit],
    staleTime: 0,
    enabled: !!depositContractAddress && !!selfDeposit,
  });
};

export const useMyBuyingPower = ({
  depositContractAddress,
  type,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
}) => {
  const { data: { baseDeposited = 0, boosted } = {} } = useSelfDepositedAmountQuery({
    depositContractAddress,
    type,
  });
  return useQuery({
    queryFn: () => getMyBuyingPower(baseDeposited, boosted),
    queryKey: ['myBuyingPower', depositContractAddress, type, baseDeposited, boosted],
    staleTime: 0,
    enabled: !!depositContractAddress,
  });
};
